import { useQuery } from '@tanstack/react-query';
import { getPaymentHandleConfig } from '@app/dashboard/services/inner_layout/payme';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import type { IPaymentHandleConfig } from '@app/dashboard/types/innerLayout/payme';
import type { UseQueryOptions } from '@tanstack/react-query';

const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes

const fetchPaymentHandleConfig = async (): Promise<IPaymentHandleConfig | null> => {
  try {
    const res = await getPaymentHandleConfig();
    const configArray = res.data;
    return Array.isArray(configArray) && configArray.length > 0
      ? configArray[0]
      : null;
  } catch {
    return null;
  }
};

export type UsePaymentHandleConfigOptions = Pick<
  UseQueryOptions<IPaymentHandleConfig | null>,
  | 'enabled'
  | 'staleTime'
  | 'refetchOnMount'
  | 'refetchOnWindowFocus'
  | 'retry'
>;

const defaultOptions: UsePaymentHandleConfigOptions = {
  enabled: true,
  staleTime: DEFAULT_STALE_TIME,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
};

/**
 * Shared hook for fetching payment handle config.
 */
export const usePaymentHandleConfig = (
  options: UsePaymentHandleConfigOptions = {}
) => {
  const mergedOptions = { ...defaultOptions, ...options };

  return useQuery<IPaymentHandleConfig | null>({
    queryKey: [QUERY_KEYS.GET_PAYMENT_HANDLE_CONFIG],
    queryFn: fetchPaymentHandleConfig,
    enabled: mergedOptions.enabled,
    staleTime: mergedOptions.staleTime,
    refetchOnMount: mergedOptions.refetchOnMount,
    refetchOnWindowFocus: mergedOptions.refetchOnWindowFocus,
    retry: mergedOptions.retry,
  });
};
